// Copyright (c) 2025 WiseTime. All rights reserved.

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import * as Actions from "./actions"
import { RootState } from "../../app/store"
import {
  GetTrainingNarrativeRequest,
  NarratePreferences,
  TrainingGroup,
  TrainingNarrative,
  TrainingRow,
} from "../../generated/wisetime/user/assistant/v4/preferences_pb"
import { TranslateLanguage } from "../../generated/wisetime/user/assistant/v4/assistant_pb"

export const toNarratePreferencesMessage = (narratePreferences: NarratePreferences.AsObject): NarratePreferences => {
  const result = new NarratePreferences()
    .setNarrateLength(narratePreferences.narrateLength)
    .setTense(narratePreferences.tense)
  if (narratePreferences.language !== undefined
    && narratePreferences.language !== TranslateLanguage.TRANSLATE_LANGUAGE_UNSPECIFIED) {
    result.setLanguage(narratePreferences.language)
  }
  if (narratePreferences.customPrompt) {
    result.setCustomPrompt(narratePreferences.customPrompt)
  }
  return result
}

export const toTrainingGroupMessage = (trainingGroup: TrainingGroup.AsObject): TrainingGroup =>
  new TrainingGroup()
    .setId(trainingGroup.id)
    .setRowsList(trainingGroup.rowsList.map(r => (
      new TrainingRow()
        .setIconUrl(r.iconUrl)
        .setAppName(r.appName)
        .setWindowTitle(r.windowTitle)
    )))

type TrainingNarrativesCache = {
  [id: string]: TrainingNarrative.AsObject,
}

export type State = {
  defaultNarratePreferences: NarratePreferences.AsObject | undefined,
  savedNarratePreferences: NarratePreferences.AsObject | undefined,
  previewNarratePreferences: NarratePreferences.AsObject | undefined,
  trainingNarrativesCache: TrainingNarrativesCache,
  trainingGroups: TrainingGroup.AsObject[],
  replacedGroupIds: string[],
}

export const initialState: State = {
  defaultNarratePreferences: undefined,
  savedNarratePreferences: undefined,
  previewNarratePreferences: undefined,
  trainingNarrativesCache: {},
  trainingGroups: [],
  replacedGroupIds: [],
}

export const slice = createSlice({
  name: "narratePreferences",
  initialState,
  reducers: {
    setDefaultNarratePreferences: (state, action: PayloadAction<NarratePreferences.AsObject>): void => {
      state.defaultNarratePreferences = action.payload
    },
    setSavedNarratePreferences: (state, action: PayloadAction<NarratePreferences.AsObject | undefined>): void => {
      state.savedNarratePreferences = action.payload
    },
    setPreviewNarratePreferences: (state, action: PayloadAction<NarratePreferences.AsObject | undefined>): void => {
      state.previewNarratePreferences = action.payload
    },
    setTrainingGroups: (state, action: PayloadAction<TrainingGroup.AsObject[]>): void => {
      state.trainingGroups = action.payload
    },
    setTrainingGroup: (state, action: PayloadAction<{
      trainingGroup: TrainingGroup.AsObject,
      replaceGroupId: string,
    }>): void => {
      const index = state.trainingGroups.findIndex(g => g.id === action.payload.replaceGroupId)
      if (index !== -1) {
        state.trainingGroups[index] = action.payload.trainingGroup
      }
    },
    setTrainingNarrative: (state, action: PayloadAction<TrainingNarrative.AsObject>): void => {
      const id = action.payload.trainingGroup?.id
      if (!id) {
        return
      }
      state.trainingNarrativesCache[id] = action.payload
    },
    resetReplacedGroupIds: (state) => {
      state.replacedGroupIds = initialState.replacedGroupIds
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        Actions.listTrainingGroups,
        (state, action: PayloadAction<Actions.ListTrainingGroupsPayload>): void => {
          const index = state.replacedGroupIds.findIndex(id => id === action.payload.replaceGroupId)
          if (action.payload.replaceGroupId && index === -1) {
            state.replacedGroupIds.push(action.payload.replaceGroupId)
          }
        },
      )
      .addCase(
        Actions.getTrainingNarrative,
        (state, action: PayloadAction<GetTrainingNarrativeRequest>): void => {
          const existingItem = state.trainingNarrativesCache[action.payload.toObject().trainingGroup?.id ?? ""]
          if (existingItem) {
            existingItem.narrative = ""
          }
        },
      )
  },
})

export const {
  setDefaultNarratePreferences,
  setSavedNarratePreferences,
  setPreviewNarratePreferences,
  setTrainingGroups,
  setTrainingGroup,
  setTrainingNarrative,
  resetReplacedGroupIds,
} = slice.actions

export default slice.reducer

export const selectDefaultNarratePreferences = (state: RootState): NarratePreferences.AsObject | undefined =>
  state.narratePreferences.defaultNarratePreferences
export const selectSavedNarratePreferences = (state: RootState): NarratePreferences.AsObject | undefined =>
  state.narratePreferences.savedNarratePreferences
export const selectTrainingNarratives = (state: RootState): TrainingNarrative.AsObject[] =>
  Object.values(state.narratePreferences.trainingNarrativesCache)
export const selectReplacedGroupIds = (state: RootState): string[] =>
  state.narratePreferences.replacedGroupIds
export type TrainingEntity = {
  group: TrainingGroup.AsObject,
  narrative: string | undefined,
}
export const selectTrainingEntities = (state: RootState): TrainingEntity[] =>
  state.narratePreferences.trainingGroups.map(trainingGroup => ({
    group: trainingGroup,
    narrative: state.narratePreferences.trainingNarrativesCache[trainingGroup.id]?.narrative,
  }))
